import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Users, Tag, BarChart3, Shield, Search, Filter, Edit2, Save, X, ChevronDown, ChevronUp, MapPin, DollarSign, Activity, TrendingUp } from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_URL || 'https://www.grad-job.com/api'

// 标签颜色映射
const TAG_COLORS = {
  gray: 'bg-gray-100 text-gray-700',
  blue: 'bg-blue-100 text-blue-700',
  green: 'bg-green-100 text-green-700',
  purple: 'bg-purple-100 text-purple-700',
  orange: 'bg-orange-100 text-orange-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  red: 'bg-red-100 text-red-700',
  gold: 'bg-amber-100 text-amber-700'
}

const AdminTagsPage = () => {
  const [admin, setAdmin] = useState(null)
  const [users, setUsers] = useState([])
  const [stats, setStats] = useState(null)
  const [categories, setCategories] = useState(null)
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({ keyword: '', tag: '', region: '' })
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 })
  const [selectedUser, setSelectedUser] = useState(null)
  const [editTags, setEditTags] = useState(false)
  const [newTags, setNewTags] = useState([])
  const [loginForm, setLoginForm] = useState({ username: '', password: '' })
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [adminsList, setAdminsList] = useState([])
  const [showAdminManager, setShowAdminManager] = useState(false)
  const [editAdmin, setEditAdmin] = useState(null)

  // 登录
  const handleLogin = async (e) => {
    e.preventDefault()
    try {
      const res = await fetch(API_BASE + '/admin/tags/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      })
      const data = await res.json()
      if (data.status === 200) {
        localStorage.setItem('adminToken', data.data.token)
        setAdmin(data.data.admin)
        setIsLoggedIn(true)
      } else {
        alert(data.message)
      }
    } catch (err) {
      alert('登录失败')
    }
  }

  // 获取token
  const getToken = () => localStorage.getItem('adminToken')

  // 获取用户列表
  const fetchUsers = async (page = 1) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(filters.keyword && { keyword: filters.keyword }),
        ...(filters.tag && { tag: filters.tag }),
        ...(filters.region && { region: filters.region })
      })
      const res = await fetch(API_BASE + '/admin/tags/users?' + params, {
        headers: { 'Authorization': 'Bearer ' + getToken() }
      })
      const data = await res.json()
      if (data.status === 200) {
        setUsers(data.data.list)
        setPagination(data.data.pagination)
        setStats(data.data.stats)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // 获取统计数据
  const fetchStats = async () => {
    try {
      const res = await fetch(API_BASE + '/admin/tags/stats', {
        headers: { 'Authorization': 'Bearer ' + getToken() }
      })
      const data = await res.json()
      if (data.status === 200) setStats(data.data)
    } catch (err) {
      console.error(err)
    }
  }

  // 获取标签分类
  const fetchCategories = async () => {
    try {
      const res = await fetch(API_BASE + '/admin/tags/categories', {
        headers: { 'Authorization': 'Bearer ' + getToken() }
      })
      const data = await res.json()
      if (data.status === 200) setCategories(data.data)
    } catch (err) {
      console.error(err)
    }
  }

  // 获取管理员列表（仅总管理员）
  const fetchAdmins = async () => {
    try {
      const res = await fetch(API_BASE + '/admin/tags/admins', {
        headers: { 'Authorization': 'Bearer ' + getToken() }
      })
      const data = await res.json()
      if (data.status === 200) setAdminsList(data.data)
    } catch (err) {
      console.error(err)
    }
  }

  // 更新用户标签
  const updateUserTags = async () => {
    if (!selectedUser) return
    try {
      const res = await fetch(API_BASE + '/admin/tags/users/' + selectedUser.id + '/tags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + getToken()
        },
        body: JSON.stringify({ tags: newTags })
      })
      const data = await res.json()
      if (data.status === 200) {
        setSelectedUser(data.data)
        setEditTags(false)
        fetchUsers(pagination.page)
      }
    } catch (err) {
      alert('更新失败')
    }
  }

  // 修改管理员信息
  const updateAdmin = async () => {
    if (!editAdmin) return
    try {
      const res = await fetch(API_BASE + '/admin/tags/admins/' + editAdmin.id, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + getToken()
        },
        body: JSON.stringify({
          username: editAdmin.username,
          password: editAdmin.password,
          name: editAdmin.name,
          assignedRegions: editAdmin.assignedRegions
        })
      })
      const data = await res.json()
      if (data.status === 200) {
        setEditAdmin(null)
        fetchAdmins()
        alert('更新成功')
      } else {
        alert(data.message)
      }
    } catch (err) {
      alert('更新失败')
    }
  }

  useEffect(() => {
    if (isLoggedIn) {
      fetchUsers()
      fetchStats()
      fetchCategories()
      if (admin?.role === 'super') fetchAdmins()
    }
  }, [isLoggedIn])

  useEffect(() => {
    if (isLoggedIn) {
      const timer = setTimeout(() => fetchUsers(1), 300)
      return () => clearTimeout(timer)
    }
  }, [filters.keyword])

  // 登录页面
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield size={32} className="text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">标签管理系统</h1>
            <p className="text-sm text-gray-500 mt-2">用户标签管理与转化分析</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={e => setLoginForm({ ...loginForm, username: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="freshgrad / freshgrad1-5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">密码</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="123456"
              />
            </div>
            <button
              type="submit"
              className="w-full py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              登录
            </button>
          </form>
          <div className="mt-4 text-xs text-gray-400 text-center">
            <p>默认账号：freshgrad (总管理员) / freshgrad1-5 (分级管理员)</p>
            <p>默认密码：123456</p>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* 头部 */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
            <Tag size={24} className="text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">用户标签管理</h1>
            <p className="text-sm text-gray-500">
              {admin?.name} | {admin?.role === 'super' ? '总管理员' : '分级管理员'}
              {!admin?.canViewAll && ` | 管辖区域：${admin?.assignedRegions?.join('、')}`}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {admin?.role === 'super' && (
            <button
              onClick={() => { setShowAdminManager(!showAdminManager); fetchAdmins() }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              <Shield size={16} className="inline mr-1" />
              管理员设置
            </button>
          )}
          <button
            onClick={() => { localStorage.removeItem('adminToken'); setIsLoggedIn(false) }}
            className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50"
          >
            退出
          </button>
        </div>
      </div>

      {/* 管理员管理面板（仅总管理员） */}
      {showAdminManager && admin?.role === 'super' && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-white rounded-xl border border-gray-200 p-6 mb-6"
        >
          <h2 className="text-lg font-bold text-gray-900 mb-4">管理员管理</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">账号</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">名称</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">角色</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">管辖区域</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {adminsList.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{a.username}</td>
                    <td className="px-4 py-3 text-sm">{a.name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${a.role === 'super' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                        {a.role === 'super' ? '总管理员' : '分级管理员'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {a.canViewAll ? '全部' : a.assignedRegions?.join('、')}
                    </td>
                    <td className="px-4 py-3">
                      {a.role !== 'super' && (
                        <button
                          onClick={() => setEditAdmin({ ...a, password: '' })}
                          className="text-primary-600 hover:text-primary-700 text-sm"
                        >
                          <Edit2 size={14} className="inline mr-1" />编辑
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 编辑管理员弹窗 */}
          {editAdmin && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 w-full max-w-md">
                <h3 className="text-lg font-bold mb-4">编辑管理员</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">用户名</label>
                    <input
                      value={editAdmin.username}
                      onChange={e => setEditAdmin({ ...editAdmin, username: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">新密码（留空不修改）</label>
                    <input
                      type="password"
                      value={editAdmin.password}
                      onChange={e => setEditAdmin({ ...editAdmin, password: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="输入新密码"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">名称</label>
                    <input
                      value={editAdmin.name}
                      onChange={e => setEditAdmin({ ...editAdmin, name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">管辖区域（用逗号分隔）</label>
                    <input
                      value={editAdmin.assignedRegions?.join('、')}
                      onChange={e => setEditAdmin({ ...editAdmin, assignedRegions: e.target.value.split('、') })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button onClick={() => setEditAdmin(null)} className="px-4 py-2 border rounded-lg">取消</button>
                  <button onClick={updateAdmin} className="px-4 py-2 bg-primary-600 text-white rounded-lg">保存</button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* 统计卡片 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Users size={20} className="text-primary-600" />
              <span className="text-2xl font-bold text-gray-900">{stats.totalUsers}</span>
            </div>
            <p className="text-sm text-gray-600">总用户数</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp size={20} className="text-red-600" />
              <span className="text-2xl font-bold text-red-600">{stats.conversionStats?.hotLeads || 0}</span>
            </div>
            <p className="text-sm text-gray-600">高意向用户</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <DollarSign size={20} className="text-green-600" />
              <span className="text-2xl font-bold text-green-600">{stats.paymentStats?.paid || 0}</span>
            </div>
            <p className="text-sm text-gray-600">付费用户</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <Activity size={20} className="text-purple-600" />
              <span className="text-2xl font-bold text-purple-600">{stats.conversionStats?.upsellReady || 0}</span>
            </div>
            <p className="text-sm text-gray-600">可升级用户</p>
          </div>
        </div>
      )}

      {/* 搜索筛选 */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="搜索用户姓名、手机号..."
              value={filters.keyword}
              onChange={e => setFilters({ ...filters, keyword: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <select
            value={filters.tag}
            onChange={e => setFilters({ ...filters, tag: e.target.value })}
            className="px-4 py-2 border border-gray-200 rounded-lg"
          >
            <option value="">全部标签</option>
            {categories && Object.entries(categories).map(([key, cat]) => (
              <optgroup key={key} label={cat.name}>
                {cat.tags.map(tag => (
                  <option key={tag.key} value={tag.key}>{tag.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <select
            value={filters.region}
            onChange={e => setFilters({ ...filters, region: e.target.value })}
            className="px-4 py-2 border border-gray-200 rounded-lg"
          >
            <option value="">全部地区</option>
            {admin?.assignedRegions?.map(region => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>
        </div>
      </div>

      {/* 用户列表 */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full mx-auto"></div></div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">用户信息</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">地区</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">测评</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">付费</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">标签</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500">{user.phone}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center text-sm text-gray-600">
                          <MapPin size={14} className="mr-1" />{user.region}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm ${user.assessmentScore >= 80 ? 'text-purple-600 font-medium' : user.assessmentScore > 0 ? 'text-gray-600' : 'text-gray-400'}`}>
                          {user.assessmentScore > 0 ? user.assessmentScore + '分' : '未测评'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm ${user.totalPaid > 0 ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                          {user.totalPaid > 0 ? '¥' + user.totalPaid : '未付费'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {user.tags?.map(tag => (
                            <span key={tag.key} className={`px-2 py-0.5 rounded text-xs ${TAG_COLORS[tag.color] || TAG_COLORS.gray}`}>
                              {tag.label}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => { setSelectedUser(user); setNewTags(user.tags || []) }}
                          className="text-primary-600 hover:text-primary-700 text-sm"
                        >
                          <Edit2 size={14} className="inline mr-1" />管理标签
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 分页 */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-center p-4 space-x-2">
                <button
                  onClick={() => fetchUsers(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-4 py-2 border rounded-lg text-sm disabled:opacity-50"
                >上一页</button>
                <span className="text-sm text-gray-600">{pagination.page} / {pagination.pages}</span>
                <button
                  onClick={() => fetchUsers(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                  className="px-4 py-2 border rounded-lg text-sm disabled:opacity-50"
                >下一页</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 标签编辑弹窗 */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">管理标签 - {selectedUser.name}</h3>
              <button onClick={() => setSelectedUser(null)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">地区：</span> {selectedUser.region}</div>
                <div><span className="text-gray-500">测评：</span> {selectedUser.assessmentScore > 0 ? selectedUser.assessmentScore + '分' : '未测评'}</div>
                <div><span className="text-gray-500">付费：</span> {selectedUser.totalPaid > 0 ? '¥' + selectedUser.totalPaid : '未付费'}</div>
                <div><span className="text-gray-500">注册：</span> {new Date(selectedUser.registerAt).toLocaleDateString()}</div>
              </div>
            </div>

            <div className="space-y-4">
              {categories && Object.entries(categories).map(([catKey, cat]) => (
                <div key={catKey}>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">{cat.name}</h4>
                  <div className="flex flex-wrap gap-2">
                    {cat.tags.map(tag => {
                      const isSelected = newTags.some(t => t.key === tag.key)
                      return (
                        <button
                          key={tag.key}
                          onClick={() => {
                            if (isSelected) {
                              setNewTags(newTags.filter(t => t.key !== tag.key))
                            } else {
                              setNewTags([...newTags, { key: tag.key, label: tag.label, category: catKey, color: tag.color }])
                            }
                          }}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            isSelected
                              ? (TAG_COLORS[tag.color] || TAG_COLORS.gray) + ' ring-2 ring-offset-1 ring-primary-500'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {tag.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setSelectedUser(null)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm"
              >
                取消
              </button>
              <button
                onClick={updateUserTags}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm flex items-center"
              >
                <Save size={14} className="mr-1" />保存标签
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default AdminTagsPage
